import React from 'react';
import { Link } from 'react-router-dom';
import {
  Target, Eye, Award, Users, CheckCircle, ArrowRight, Briefcase, Shield
} from 'lucide-react';

const values = [
  {
    icon: Shield,
    title: 'Integrity',
    description: 'Unwavering commitment to ethical practices and transparency in all our operations',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'Delivering superior quality and innovative solutions that exceed expectations',
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'Building strong partnerships with clients and stakeholders for mutual success',
  },
  {
    icon: Target,
    title: 'Impact',
    description: 'Creating lasting positive change in communities and organizations we serve',
  },
];

const expertise = [
  'Land Resource Management & Administration',
  'Real Estate Portfolio Optimization',
  'Project Financing & Advisory',
  'Revenue Collection Systems',
  'Urban Planning & Development',
  'Government Consulting Services',
];

export default function AboutPage() {
  return (
    <>
      <div className="relative bg-slate-900 text-white py-24">
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{
              backgroundImage: 'url("https://images.unsplash.com/photo-1497366216548-37526070297c?w=2000&q=80")',
            }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              About MACOS
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Nigeria's trusted partner in urban management and consultancy excellence
            </p>
          </div>
        </div>
      </div>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg
                            bg-emerald-50 mb-6">
                <Briefcase className="h-6 w-6 text-emerald-600" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Who We Are
              </h2>
              <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                <p>
                  MACOS Urban Management Consultancy is a registered consulting firm with the
                  Corporate Affairs Commission, Abuja, specializing in comprehensive land resource
                  management, real estate portfolio optimization, and strategic project financing.
                </p>
                <p>
                  With over 15 years of experience, we have established ourselves as a trusted
                  partner for government agencies and private sector organizations across Nigeria.
                  Our team of experts brings deep industry knowledge and innovative solutions to
                  every project we undertake.
                </p>
                <p>
                  We pride ourselves on our commitment to excellence, integrity, and delivering
                  measurable results that drive organizational success and community development.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80"
                alt="Team collaboration"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div className="bg-white p-10 rounded-xl shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <Target className="h-7 w-7 text-emerald-600" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900">Our Mission</h2>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed">
                To deliver innovative, sustainable, and client-focused consultancy solutions that
                transform urban management practices and drive economic development across Nigeria.
              </p>
            </div>

            <div className="bg-white p-10 rounded-xl shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <Eye className="h-7 w-7 text-emerald-600" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900">Our Vision</h2>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed">
                To be the premier urban management consultancy in Nigeria, recognized for our
                expertise, innovation, and unwavering commitment to excellence and integrity.
              </p>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The principles that guide our work and define our commitment to clients
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition text-center"
                >
                  <div className="w-16 h-16 rounded-xl bg-emerald-50 flex items-center justify-center
                                mx-auto mb-6">
                    <Icon className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Our Expertise
              </h2>
              <p className="text-lg text-slate-600">
                Comprehensive consultancy services backed by years of experience
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {expertise.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-slate-50 p-6 rounded-lg hover:bg-slate-100 transition"
                >
                  <CheckCircle className="h-6 w-6 text-emerald-500 flex-shrink-0" />
                  <p className="text-slate-700 font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-xl text-emerald-100 mb-10 leading-relaxed">
            Let's discuss how MACOS can help transform your organization
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-white text-emerald-600 px-10 py-4 rounded-lg
                     font-semibold text-lg hover:bg-gray-50 transition shadow-lg"
          >
            Get in Touch
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </>
  );
}
